/** Gmail’in yaygın kabul ettiği ek uzantıları / MIME’lar. Toplam sınır 20 MB. */
export const MAIL_ATTACHMENT_MAX_TOTAL_BYTES = 20 * 1024 * 1024

export const MAIL_ATTACHMENT_EXTENSIONS = [
  // belgeler
  '.doc',
  '.docx',
  '.pdf',
  '.txt',
  '.rtf',
  '.odt',
  '.pages',
  // tablolar
  '.xls',
  '.xlsx',
  '.csv',
  '.ods',
  '.numbers',
  // sunum
  '.ppt',
  '.pptx',
  '.odp',
  '.key',
  // görüntü
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.webp',
  '.svg',
  '.tif',
  '.tiff',
  '.heic',
  // arşiv
  '.zip',
  '.rar',
  '.7z',
  '.tar',
  '.gz',
  // medya
  '.mp3',
  '.mp4',
  '.mov',
  '.avi',
  '.wav',
  '.m4a',
  '.webm',
  // diğer
  '.html',
  '.htm',
  '.ics',
  '.json',
  '.xml',
  '.eml',
] as const

export const MAIL_ATTACHMENT_ACCEPT = MAIL_ATTACHMENT_EXTENSIONS.join(',')

export type MailAttachmentMeta = {
  id: string
  name: string
  size: number
  mime: string
  /** Sunucuda dosyası duruyorsa indirilebilir (eski kayıtlarda yalnız ad/boyut vardır). */
  downloadable?: boolean
}

export type ComposeAttachment = MailAttachmentMeta & {
  /** Bellekte; sessionStorage’a yazılmaz. */
  file?: File
}

export function fileExtension(name: string): string {
  const i = name.lastIndexOf('.')
  if (i < 0) return ''
  return name.slice(i).toLowerCase()
}

export function isAllowedAttachment(file: File): boolean {
  const ext = fileExtension(file.name)
  return MAIL_ATTACHMENT_EXTENSIONS.includes(ext as (typeof MAIL_ATTACHMENT_EXTENSIONS)[number])
}

export function totalAttachmentBytes(items: { size: number }[]): number {
  return items.reduce((sum, a) => sum + a.size, 0)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10_240 ? 1 : 0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10_485_760 ? 1 : 0)} MB`
}

export function toAttachmentMeta(file: File): ComposeAttachment {
  return {
    id: `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: file.name,
    size: file.size,
    mime: file.type || 'application/octet-stream',
    file,
  }
}

export function stripAttachmentFiles(items: ComposeAttachment[]): MailAttachmentMeta[] {
  return items.map(({ id, name, size, mime }) => ({ id, name, size, mime }))
}

export type AttachmentPickError = 'type' | 'totalSize'

export function validateNewAttachments(
  existing: { size: number }[],
  incoming: File[],
): { ok: ComposeAttachment[]; error?: AttachmentPickError; rejectedName?: string } {
  const accepted: ComposeAttachment[] = []
  let running = totalAttachmentBytes(existing)

  for (const file of incoming) {
    if (!isAllowedAttachment(file)) {
      return { ok: accepted, error: 'type', rejectedName: file.name }
    }
    if (running + file.size > MAIL_ATTACHMENT_MAX_TOTAL_BYTES) {
      return { ok: accepted, error: 'totalSize', rejectedName: file.name }
    }
    running += file.size
    accepted.push(toAttachmentMeta(file))
  }
  return { ok: accepted }
}
